import { Store } from 'vuex';
import { IPulseVersion } from '../types/internal';

export class VersionHelper {

	public colours: Map<string, string> = new Map();
	public status_types: Map<string, string> = new Map();

	public constructor(protected store: Store<any>) {
		this.colours.set('green', '#25c281');
		this.colours.set('orange', '#e89924');
		this.colours.set('red', '#f1592a');

		this.status_types.set('green', 'under18months');
		this.status_types.set('orange', 'over18months');
		this.status_types.set('red', 'over24months');
	}

	/**
	 * Will take the current version and find the next available version.
	 *
	 * @param version The current version.
	 * @returns The next version.
	 */
	public getNextVersion(version: string): string {
		const versionList: Array<IPulseVersion> = this.getPulseVersions();
		const indexMatch: number = versionList.map((x: IPulseVersion) => x.previous_version).indexOf(version);
		if (indexMatch > -1) return versionList[indexMatch].name;
		return 'Unknown';
	}

	/**
	 * Will take the current version and find the previous version.
	 *
	 * @param version The current version.
	 * @returns The previous version.
	 */
	public getPreviousVersion(version: string): string {
		const versionList: Array<IPulseVersion> = this.getPulseVersions();
		const indexMatch: number = versionList.map((x: IPulseVersion) => x.name).indexOf(version);
		if (indexMatch > -1) return versionList[indexMatch].previous_version;
		return 'Unknown';
	}

	/**
	 * Will take the current version and will then check the releaseDate and
	 * return a colour based on the following.
	 *
	 * Release Date < 18 Months = Green.
	 * Release Date >= 18 Months & < 24 Months = Orange.
	 * Release Date >= 24 Months = Red.
	 *
	 * @param version The current version.
	 * @param return_type colour code or class name
	 * @returns The colour hex string for that version.
	 */
	public getVersionColour(version: string, return_type = 'colour'): string {
		// Get the release date.
		const versionList: Array<IPulseVersion> = this.getPulseVersions();
		const indexMatch: number = versionList.map((x: IPulseVersion) => x.name).indexOf(String(version));
		if (indexMatch === -1) return (return_type === 'colour' ? String(this.colours.get('red')) : String(this.status_types.get('red')));
		const releaseDate: string | null = versionList[indexMatch].release_date;
		if (releaseDate === null) return (return_type === 'colour' ? String(this.colours.get('green')) : String(this.status_types.get('green')));

		// Now let's check the release date.
		const today = new Date().valueOf();
		const released = new Date(this.toAmericanFormat(releaseDate)).valueOf();

		// Let's get a difference.
		const timeDifference = today - released;

		if (timeDifference < 47340000000) return (return_type === 'colour' ? String(this.colours.get('green')) : String(this.status_types.get('green')));
		if (timeDifference >= 47340000000 && timeDifference < 63120000000) return (return_type === 'colour' ? String(this.colours.get('orange')) : String(this.status_types.get('orange')));
		if (timeDifference > 63120000000) return (return_type === 'colour' ? String(this.colours.get('red')) : String(this.status_types.get('red')));

		// If we are still processing then return red.
		return (return_type === 'colour' ? String(this.colours.get('red')) : String(this.status_types.get('red')));
	}

	private getPulseVersions(): Array<IPulseVersion> {
		return this.store.state.configuration.versionsData;
	}

	private toAmericanFormat(date: string): string {
		const parts: Array<string> = date.split('/');
		return `${parts[1]}/${parts[0]}/${parts[2]}`;
	}
}
